import { translator } from "../../translation/translator";
import { FloatingChatForm } from "./floating-chat-form";

const floatingChatForm = new FloatingChatForm();

// Both the shared ScrollLock utility and the product-search picker's own ad
// hoc lock (search-menu-setup.ts's showMenu()) set this same body style when
// any modal/overlay opens — reusing it here avoids coupling to every
// individual modal subsystem just to know when one is open.
function isAnyModalOpen(): boolean {
    return document.body.style.overflow === "hidden";
}

function isProductPickerOpen(): boolean {
    return floatingChatForm.getProductPickers().some((picker) => picker.classList.contains("visible"));
}

/**
 * Keeps the floating button visible (overriding the scroll-direction
 * hide/show) for as long as any modal has body scroll locked, and swaps its
 * label to the "can't find a product?" copy specifically while the review
 * flow's product picker is the modal that's open — reverting to the page's
 * own default label (brand page vs. generic) otherwise.
 */
export function bindFloatingChatModalVisibility(button: HTMLElement): void {
    const label = floatingChatForm.getLabel(button);
    const defaultLabelKey = label?.getAttribute("data-translate") ?? null;

    const applyLabel = (key: string): void => {
        if (!label || label.getAttribute("data-translate") === key) return;
        label.setAttribute("data-translate", key);
        label.textContent = translator.getTranslation(key);
    };

    const sync = (): void => {
        const modalOpen = isAnyModalOpen();
        button.classList.toggle("is-modal-open", modalOpen);

        if (modalOpen && isProductPickerOpen()) {
            applyLabel("product_search_ask_messenger");
        } else if (defaultLabelKey) {
            applyLabel(defaultLabelKey);
        }
    };

    sync();

    // One subtree-wide observer on <body> covers every relevant transition:
    // the shared modal-lock style toggle, the product picker's own "visible"
    // class toggle (which can happen while body overflow is ALREADY locked,
    // since the picker nests inside an already-open review-create modal), and
    // the picker container being inserted into the DOM for the first time —
    // it only exists once the AJAX-loaded review-create modal fragment has
    // been injected (post-create-modal-fragment.blade.php), not at page load,
    // so an observer attached only to pickers found at setup time would miss
    // it entirely on that (most common) flow.
    new MutationObserver(sync).observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style", "class"],
    });
}
